import React, { useState } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import { csp } from '../../assets/assets';
import { Link } from 'react-router-dom';
import { useRegister } from '../../services/auth/auth'; 
import SocialLogin from '../../services/auth/SocialLogin';
import { useAlert } from '../../context/AlertContext';


const SignUpClient = ({ onClose }) => {
  const {mutateAsync: registerUser} = useRegister();
  const setAlert = useAlert();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    role: 'user',
    client: true
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setAlert({ type: 'error', message: 'Passwords do not match' });
      return;
    }

    // Password strength validation
    const passwordRegex = /^(?=(?:[^A-Z]*[A-Z]){3})(?=(?:[^a-z]*[a-z]){2})(?=(?:\D*\d){2})(?=.*[^a-zA-Z0-9]).{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      setAlert({ type: 'error', message: 'Password must be at least 8 characters long, contain at least 3 uppercase letters, 2 lowercase letters, 2 digits, and 1 special character.' });
      return;
    }

   try {
    await registerUser(formData);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      agreeToTerms: false,
      role: 'user'
    });
    setAlert({ type: 'success', message: 'Registration successful. Please check your email to activate your account.' });
   } catch (error) {
     setAlert({ type: 'error', message: error.response?.data?.error || 'Registration failed' });
     console.log(error);
   }
  };

  return (
    <div className="font-['Poppins'] bg-[#F5F5F5] min-h-screen flex md:flex-row flex-col relative">
      {/* Image section - full width on mobile, left half on desktop */}
      <div className="w-[88%] mt-[4rem] md:mt-0 mx-auto md:w-1/2 md:min-h-screen">
        {/* Close button - visible only on mobile */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 md:hidden"
          aria-label="Close"
        >

          <Link to={"/"}>
            <X size={24} className="text-gray-800" />
          </Link>
        </button>

        <img
          src={csp}
          alt="People working together"
          className="w-full h-72 md:h-full object-cover"
        />
      </div>

      {/* Form section - full width on mobile, right half on desktop */}
      <div className="md:w-1/2 px-6 py-8 md:px-16 md:py-12 md:flex md:flex-col md:justify-center">
        <h1 className="text-2xl font-bold text-[#174568] mb-6">Sign Up</h1>

        <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
          <div className="md:flex md:space-x-4 space-y-4 md:space-y-0">
            <div className="md:w-1/2">
              <label htmlFor="firstName" className="block text-gray-700 mb-2">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter your name here"
                className="w-full p-3 
  border border-[#C1C7CD] 
  md:border-x-0 md:border-t-0 
  md:bg-[#F2F4F8] bg-transparent 
  rounded-md md:rounded-none 
  focus:outline-none 
  focus:border-[#174568] 
  md:focus:border-b-[#174568] md:focus:border-x-0 md:focus:border-t-0"

                required
              />
            </div>
            <div className="md:w-1/2">
              <label htmlFor="lastName" className="block text-gray-700 mb-2">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter surname here"
                className="w-full p-3 
                border border-[#C1C7CD] 
                md:border-x-0 md:border-t-0 
                md:bg-[#F2F4F8] bg-transparent 
                rounded-md md:rounded-none 
                focus:outline-none 
                focus:border-[#174568] 
                md:focus:border-b-[#174568] md:focus:border-x-0 md:focus:border-t-0"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-gray-700 mb-2">Email{" "}{!formData.email && <span className="sr-only">Address</span>}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email address"
              className="w-full p-3 
              border border-[#C1C7CD] 
              md:border-x-0 md:border-t-0 
              md:bg-[#F2F4F8] bg-transparent 
              rounded-md md:rounded-none 
              focus:outline-none 
              focus:border-[#174568] 
              md:focus:border-b-[#174568] md:focus:border-x-0 md:focus:border-t-0"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-gray-700 mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter password"
                className="w-full p-3 
                border border-[#C1C7CD] 
                md:border-x-0 md:border-t-0 
                md:bg-[#F2F4F8] bg-transparent 
                rounded-md md:rounded-none 
                focus:outline-none 
                focus:border-[#174568] 
                md:focus:border-b-[#174568] md:focus:border-x-0 md:focus:border-t-0"
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-3 flex items-center md:right-0 md:pr-3"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} className="text-gray-500" /> : <Eye size={20} className="text-gray-500" />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-gray-700 mb-2">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm password"
                className="w-full p-3 
                border border-[#C1C7CD] 
                md:border-x-0 md:border-t-0 
                md:bg-[#F2F4F8] bg-transparent 
                rounded-md md:rounded-none 
                focus:outline-none 
                focus:border-[#174568] 
                md:focus:border-b-[#174568] md:focus:border-x-0 md:focus:border-t-0"
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-3 flex items-center md:right-0 md:pr-3"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={20} className="text-gray-500" /> : <Eye size={20} className="text-gray-500" />}
              </button>
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="agreeToTerms"
              name="agreeToTerms"
              checked={formData.agreeToTerms}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              required
            />
            <label htmlFor="agreeToTerms" className="ml-2 block text-sm text-gray-700">
              I agree to the terms and condition
            </label>
          </div>

           <button
            type="submit"
            className="w-full bg-[#174568] hover:bg-gray-800 text-white font-medium py-3 px-4 transition-colors"
          >
            Sign up
          </button> 
        

          <div className="text-center text-gray-600 my-4">or</div>

            <SocialLogin/>

          <div className="text-left mt-6">
            <Link to={"/clients/signin"} className="text-[#001D6C] hover:underline">Already have an account?</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignUpClient;